import { UnstyledButton } from "src/ui";
import type { GroupedTableColumn, GroupedTableSortState } from "../types";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";

type GroupedTableSortButtonProps<TRow> = {
    column: GroupedTableColumn<TRow>;
    sort?: GroupedTableSortState<TRow> | undefined;
    onChange: (sort?: GroupedTableSortState<TRow>) => void;
};

function getNextSort<TRow>(
    column: GroupedTableColumn<TRow>,
    sort?: GroupedTableSortState<TRow> | undefined,
): GroupedTableSortState<TRow> | undefined {
    if (!sort || sort.columnId !== column.id) {
        return {
            columnId: column.id,
            direction: "asc",
        };
    }

    if (sort.direction === "asc") {
        return {
            columnId: column.id,
            direction: "desc",
        };
    }

    return undefined;
}

function getSortLabel<TRow>(
    column: GroupedTableColumn<TRow>,
    sort?: GroupedTableSortState<TRow> | undefined,
): string {
    if (!sort || sort.columnId !== column.id) {
        return "Sort";
    }

    return sort.direction === "asc"
        ? "Sort ascending"
        : "Sort descending";
}

function GroupedTableSortButton<TRow>({
    column,
    onChange,
    sort,
}: GroupedTableSortButtonProps<TRow>): JSX.Element {
    const isActive = sort?.columnId === column.id;

    const onAscendingClick = () => {
        onChange({
            columnId: column.id,
            direction: isActive && sort?.direction === "asc"
                ? "desc"
                : "asc",
        });
    };

    const onDescendingClick = () => {
        onChange({
            columnId: column.id,
            direction: isActive && sort?.direction === "desc"
                ? "asc"
                : "desc",
        });
    };
    
    return (
        <div className="GroupedTableSortButton">
            <UnstyledButton
                className="GroupedTableSortButton-button"
                onClick={onAscendingClick}
                title={getSortLabel(column, sort)}>
                <Visage2Icon
                    className={isActive && sort?.direction === "asc" ? "active" : ""}
                    icon="arrow-up-04"
                    size="small" />
            </UnstyledButton>
            <UnstyledButton
                className="GroupedTableSortButton-button"
                onClick={onDescendingClick}
                title={getSortLabel(column, sort)}>
                <Visage2Icon
                    className={isActive && sort?.direction === "desc" ? "active" : ""}
                    icon="arrow-down-04"
                    size="small" />
            </UnstyledButton>
        </div>
    );
}

export default GroupedTableSortButton;
